/**
 * Shared Storefront GraphQL helpers for FCD brand collection blocks.
 * Usage:
 *   const brands = await window.FCDBrandsStorefront.fetchCollectionsWithBrandLogo({
 *     storefrontAccessToken: '...',
 *     namespace: 'custom',
 *     key: 'brand_logo',
 *     onlyWithLogo: true,
 *   });
 */
(function (global) {
  const DEFAULT_API_VERSION = '2025-01';
  const PAGE_SIZE = 250;

  const COLLECTIONS_WITH_BRAND_LOGO_QUERY = `
    query GetCollectionsWithBrandLogo(
      $first: Int!
      $after: String
      $namespace: String!
      $key: String!
    ) {
      collections(first: $first, after: $after, sortKey: TITLE) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            onlineStoreUrl
            brandLogo: metafield(namespace: $namespace, key: $key) {
              value
              type
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  function getEndpoint(apiVersion) {
    return '/api/' + (apiVersion || DEFAULT_API_VERSION) + '/graphql.json';
  }

  function mapCollectionNode(node) {
    var logo = node && node.brandLogo;
    var image = logo && logo.reference && logo.reference.image;
    return {
      id: node.id,
      title: node.title,
      handle: node.handle,
      url: node.onlineStoreUrl || '/collections/' + node.handle,
      brandLogoUrl: image && image.url ? image.url : null,
      brandLogoAlt: (image && image.altText) || node.title,
      hasBrandLogo: Boolean(image && image.url),
      raw: node
    };
  }

  /**
   * @param {object} options
   * @param {string} options.storefrontAccessToken
   * @param {string} [options.apiVersion]
   * @param {string} [options.namespace='custom']
   * @param {string} [options.key='brand_logo']
   * @param {number} [options.first=250]
   * @param {string|null} [options.after=null]
   * @returns {Promise<{collections: Array, pageInfo: object, errors?: Array}>}
   */
  async function fetchCollectionsPage(options) {
    var token = options && options.storefrontAccessToken;
    if (!token) {
      throw new Error('FCDBrandsStorefront: storefrontAccessToken is required');
    }

    var namespace = (options && options.namespace) || 'custom';
    var key = (options && options.key) || 'brand_logo';
    var first = (options && options.first) || PAGE_SIZE;
    var after = (options && options.after) || null;
    var endpoint = getEndpoint(options && options.apiVersion);

    var response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query: COLLECTIONS_WITH_BRAND_LOGO_QUERY,
        variables: {
          first: first,
          after: after,
          namespace: namespace,
          key: key
        }
      })
    });

    var result = await response.json();
    if (!response.ok) {
      throw new Error('FCDBrandsStorefront: HTTP ' + response.status);
    }
    if (result.errors && result.errors.length) {
      return {
        collections: [],
        pageInfo: { hasNextPage: false, endCursor: null },
        errors: result.errors
      };
    }

    var connection = result.data && result.data.collections;
    var edges = (connection && connection.edges) || [];
    return {
      collections: edges.map(function (edge) {
        return mapCollectionNode(edge.node);
      }),
      pageInfo: (connection && connection.pageInfo) || {
        hasNextPage: false,
        endCursor: null
      },
      errors: null
    };
  }

  /**
   * Fetches all pages (or until maxPages), optionally filtering to logo-only.
   * @param {object} options - same as fetchCollectionsPage, plus:
   * @param {boolean} [options.onlyWithLogo=true]
   * @param {number} [options.maxPages=20]
   * @param {function} [options.onPage] - callback(collectionsSoFar, pageResult)
   */
  async function fetchCollectionsWithBrandLogo(options) {
    var onlyWithLogo = !options || options.onlyWithLogo !== false;
    var maxPages = (options && options.maxPages) || 20;
    var after = null;
    var all = [];

    for (var page = 0; page < maxPages; page += 1) {
      var pageResult = await fetchCollectionsPage(
        Object.assign({}, options, { after: after })
      );

      if (pageResult.errors) {
        return {
          collections: all,
          errors: pageResult.errors,
          pageInfo: pageResult.pageInfo
        };
      }

      var batch = pageResult.collections;
      if (onlyWithLogo) {
        batch = batch.filter(function (item) {
          return item.hasBrandLogo;
        });
      }
      all = all.concat(batch);

      if (typeof options.onPage === 'function') {
        options.onPage(all, pageResult);
      }

      if (!pageResult.pageInfo.hasNextPage) {
        break;
      }
      after = pageResult.pageInfo.endCursor;
    }

    all.sort(function (a, b) {
      return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
        sensitivity: 'base'
      });
    });

    return {
      collections: all,
      errors: null,
      pageInfo: { hasNextPage: false, endCursor: after }
    };
  }

  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  function getInitialKey(title) {
    var trimmed = String(title || '').trim();
    if (!trimmed) return '#';
    var first = trimmed.charAt(0).toUpperCase();
    if (first >= 'A' && first <= 'Z') return first;
    if (first >= '0' && first <= '9') return '0-9';
    return '#';
  }

  /**
   * Groups collections alphabetically for directory UIs.
   * @param {Array<{title:string,url?:string,handle?:string}>} collections
   * @returns {{ order: string[], groups: Object<string, Array>, activeLetters: Object<string, boolean> }}
   */
  function groupByInitialLetter(collections) {
    var groups = {};
    var activeLetters = {};
    LETTERS.forEach(function (letter) {
      groups[letter] = [];
      activeLetters[letter] = false;
    });
    groups['0-9'] = [];
    activeLetters['0-9'] = false;
    groups['#'] = [];
    activeLetters['#'] = false;

    (collections || [])
      .slice()
      .sort(function (a, b) {
        return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
          sensitivity: 'base'
        });
      })
      .forEach(function (item) {
        var key = getInitialKey(item.title);
        if (!groups[key]) {
          groups[key] = [];
          activeLetters[key] = false;
        }
        groups[key].push(item);
        activeLetters[key] = true;
      });

    var order = LETTERS.concat(['0-9']);
    if (groups['#'].length) order.push('#');

    return {
      order: order,
      groups: groups,
      activeLetters: activeLetters
    };
  }

  global.FCDBrandsStorefront = {
    QUERY_COLLECTIONS_WITH_BRAND_LOGO: COLLECTIONS_WITH_BRAND_LOGO_QUERY,
    LETTERS: LETTERS,
    fetchCollectionsPage: fetchCollectionsPage,
    fetchCollectionsWithBrandLogo: fetchCollectionsWithBrandLogo,
    getInitialKey: getInitialKey,
    groupByInitialLetter: groupByInitialLetter
  };
})(typeof window !== 'undefined' ? window : globalThis);
